import { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation } from 'wouter';
import type { GameState } from '@/lib/golfEngine';
import { hydrateGameStateFromRound } from '@/lib/golfEngine';
import { getGameEngine } from '@/games/index';
import { ScorecardShell } from '@/components/ScorecardShell';
import { PressRows } from '@/components/PressRows';
import { BetStatusModal } from '@/components/BetStatusModal';
import { ResultsScreen } from '@/components/ResultsScreen';
import { BottomNav } from '@/components/BottomNav';
import { useAuth } from '@/hooks/use-auth';
import { Loader2, Eye, BarChart3, ArrowLeft, Wifi } from 'lucide-react';
import { Button } from '@/components/ui/button';

const POLL_INTERVAL = 10_000;

export default function LiveScorecard() {
  const { roundId } = useParams<{ roundId: string }>();
  const [, setLocation] = useLocation();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const [gameState, setGameState] = useState<GameState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBetStatus, setShowBetStatus] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isOrganizer, setIsOrganizer] = useState(false);
  const [isAttached, setIsAttached] = useState(false);
  const [roundGroupId, setRoundGroupId] = useState<string | null>(null);

  const fetchRound = async (isInitial = false) => {
    if (!roundId) return;
    try {
      const res = await fetch(`/api/rounds/${roundId}/live`, { credentials: 'include' });
      if (!res.ok) {
        if (res.status === 403) {
          setError("You don't have access to this round.");
        } else if (res.status === 404) {
          setError("Round not found.");
        } else {
          setError("Failed to load round data.");
        }
        setLoading(false);
        return;
      }
      const data = await res.json();
      const hydrated = await hydrateGameStateFromRound(data);
      setGameState(hydrated);
      setIsOrganizer(!!data.isOrganizer);
      setIsAttached(!!data.isAttached);
      setRoundGroupId(data.round?.groupId ?? null);
      setLastUpdated(new Date());
      setError(null);
      if (isInitial) setLoading(false);
    } catch {
      if (isInitial) {
        setError("Failed to load round data.");
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (authLoading) return;

    fetchRound(true);
    const interval = setInterval(() => fetchRound(false), POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [roundId, authLoading]);

  const engine = useMemo(() => getGameEngine(gameState?.gameType), [gameState?.gameType]);

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center" data-testid="live-loading">
        <div className="text-center space-y-3">
          <Loader2 className="w-8 h-8 animate-spin text-[#064E3B] mx-auto" />
          <p className="text-sm font-medium text-gray-600">Loading live scorecard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <>
        <div className="min-h-screen bg-gray-100 flex items-center justify-center px-6" data-testid="live-error">
          <div className="text-center space-y-4 max-w-sm">
            <Eye className="w-12 h-12 text-gray-400 mx-auto" />
            <h2 className="text-lg font-bold text-gray-900">Can't View Round</h2>
            <p className="text-sm text-gray-600">{error}</p>
            <Button onClick={() => setLocation("/")} variant="outline" data-testid="button-go-home">
              Go Home
            </Button>
          </div>
        </div>
        <BottomNav />
      </>
    );
  }

  if (!gameState) return null;

  if (gameState.isFinalized) {
    return (
      <>
        <ResultsScreen
          gameState={gameState}
          readOnly={!isOrganizer}
          isOrganizer={isOrganizer}
          isAttached={isAttached}
          groupId={roundGroupId}
        />
        <BottomNav />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white border-b border-gray-200 px-4 pb-2 sticky top-0 z-50 safe-area-top">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setLocation("/")}
              data-testid="button-back-live"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-bold text-gray-900 text-sm">
                {gameState.gameMode === 'TwoDownAuto' ? 'Two Down Auto' : 'DoublePress!'}
              </h1>
              <p className="text-xs text-gray-500">
                {gameState.matchFormat} • {gameState.startingHole === 1 ? 'Front Start' : 'Back Start'}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowBetStatus(true)}
            className="gap-2 text-base font-bold px-5 py-2"
            data-testid="button-bet-status-live"
          >
            <BarChart3 className="w-5 h-5" />
            Bet Status
          </Button>
        </div>
      </header>

      <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-2" data-testid="banner-spectator">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-emerald-700 flex-shrink-0" />
            <p className="text-xs font-medium text-emerald-800">Live Spectator View</p>
          </div>
          <div className="flex items-center gap-1.5">
            <Wifi className="w-3 h-3 text-emerald-600" />
            <p className="text-xs text-emerald-600">
              {lastUpdated ? `Updated ${formatTimeAgo(lastUpdated)}` : 'Connecting...'}
            </p>
          </div>
        </div>
      </div>

      <main className="px-4 py-4 max-w-4xl mx-auto">
        <ScorecardShell gameState={gameState} />
        <PressRows
          gameState={gameState}
          onAddPress={() => {}}
          onPressExistingPress={() => {}}
          selectedHole={null}
          readOnly
        />
      </main>

      {showBetStatus && (
        <BetStatusModal
          gameState={gameState}
          onClose={() => setShowBetStatus(false)}
        />
      )}
    </div>
  );
}

function formatTimeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 10) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ago`;
}
